import {
  PERMISSION_KEYS,
  isPermissionKey,
  permissionDefinition,
  type PermissionKey,
} from './registry';
import { scopeCovers, widerScope, type Scope } from './scope';

/** The permissions a member effectively holds, each at the widest scope granted. */
export type PermissionMap = Partial<Record<PermissionKey, Scope>>;

export interface Grant {
  permission: string;
  scope: Scope;
}

export interface Override {
  permission: string;
  effect: 'grant' | 'deny';
  scope: Scope | null;
}

export interface ResolveInput {
  /** True when one of the member's roles is the owner role. */
  isOwner: boolean;
  /** Grants from every role the member holds. */
  grants: readonly Grant[];
  /** Per-member exceptions, applied after the roles. */
  overrides?: readonly Override[];
}

/**
 * Turns roles and overrides into the member's effective permission map.
 *
 * Order matters and is fixed: roles are unioned at their widest scope, then
 * member-level grants widen, then member-level denies remove. A deny always wins —
 * it is how an agency takes one permission away from one person without building a
 * role for them. Owners hold everything and cannot be narrowed: an agency must never
 * be able to lock itself out of its own settings.
 *
 * Keys the registry no longer knows are dropped silently, so retiring a permission
 * from the code retires it everywhere without a data migration.
 */
export function resolvePermissions({
  isOwner,
  grants,
  overrides = [],
}: ResolveInput): PermissionMap {
  const map: PermissionMap = {};

  if (isOwner) {
    for (const key of PERMISSION_KEYS) map[key] = 'tenant';
    return map;
  }

  for (const grant of grants) {
    if (!isPermissionKey(grant.permission)) continue;
    widen(map, grant.permission, normalize(grant.permission, grant.scope));
  }

  for (const override of overrides) {
    if (!isPermissionKey(override.permission) || override.effect !== 'grant') continue;
    widen(map, override.permission, normalize(override.permission, override.scope ?? 'tenant'));
  }

  for (const override of overrides) {
    if (isPermissionKey(override.permission) && override.effect === 'deny') {
      delete map[override.permission];
    }
  }

  return map;
}

function widen(map: PermissionMap, key: PermissionKey, scope: Scope): void {
  const held = map[key];
  map[key] = held ? widerScope(held, scope) : scope;
}

/** Unscoped permissions are switches; they are always held at tenant scope. */
export function normalize(key: string, scope: Scope): Scope {
  return permissionDefinition(key)?.scoped ? scope : 'tenant';
}

/** Does the map hold `key`, reaching at least `scope`? Without a scope, any grant counts. */
export function can(map: PermissionMap, key: PermissionKey, scope: Scope = 'own'): boolean {
  const held = map[key];
  return held !== undefined && scopeCovers(held, scope);
}

/**
 * True when every permission in `candidate` is held by `actor` at an equal or wider
 * scope. The API uses it to stop privilege escalation: a member who may edit roles
 * can still only hand out what they hold themselves.
 */
export function isSubsetOf(candidate: PermissionMap, actor: PermissionMap): boolean {
  for (const [key, scope] of Object.entries(candidate) as [PermissionKey, Scope][]) {
    if (!can(actor, key, scope)) return false;
  }
  return true;
}

export interface RecordOwnership {
  ownerUserId: string | null;
  departmentId: string | null;
  companyId: string | null;
}

export interface Actor {
  userId: string;
  departmentIds: readonly string[];
  companyIds: readonly string[];
}

/**
 * Whether a scoped permission lets `actor` act on one particular record. The
 * operational modules (sales, visa, tasks) call this; list queries apply the same
 * rule as a SQL filter.
 */
export function canOnRecord(
  map: PermissionMap,
  key: PermissionKey,
  actor: Actor,
  record: RecordOwnership,
): boolean {
  const held = map[key];
  if (!held) return false;
  if (held === 'tenant') return true;
  if (held === 'company' && record.companyId && actor.companyIds.includes(record.companyId)) {
    return true;
  }
  if (
    held !== 'own' &&
    record.departmentId !== null &&
    actor.departmentIds.includes(record.departmentId)
  ) {
    return true;
  }
  return record.ownerUserId === actor.userId;
}

/**
 * A member's permissions with roles per company (ADR 0017): `permissions` is what
 * they hold anywhere; `globalPermissions` what holds in every company;
 * `companyPermissions` what holds in each company some role is assigned to alone,
 * or null when every role holds everywhere.
 */
export interface CompanyPermissions {
  permissions: PermissionMap;
  globalPermissions: PermissionMap;
  companyPermissions: Record<string, PermissionMap> | null;
}

/** What holds for records of one company; a record without a company follows every-company roles. */
export function permissionsInCompany(
  p: CompanyPermissions,
  companyId: string | null,
): PermissionMap {
  if (!p.companyPermissions) return p.permissions;
  return (companyId && p.companyPermissions[companyId]) || p.globalPermissions;
}

/** `canOnRecord` under the record's company's roles; inside such a company, company scope is that company. */
export function reachesRecord(
  p: CompanyPermissions,
  key: PermissionKey,
  actor: Actor,
  record: RecordOwnership,
): boolean {
  const own =
    p.companyPermissions && record.companyId ? p.companyPermissions[record.companyId] : undefined;
  return canOnRecord(
    permissionsInCompany(p, record.companyId),
    key,
    own ? { ...actor, companyIds: [record.companyId!] } : actor,
    record,
  );
}
